import { ReactNode, useCallback, useMemo, useState } from "react"

import {
    DataGridProps,
    GridColDef,
    GridComparatorFn,
    GridSortModel
} from "@mui/x-data-grid"

import { ArrowDropDownIcon, ArrowDropUpIcon } from "@l2r-front/l2r-icons"

import { DataGridPagination } from "../DataGridPagination"

import * as Styled from "./L2RTable.styled"

const DEFAULT_PAGE_SIZE = 30
const ROW_HEIGHT = 42
const ROW_SPACING = 8
const EMPTY_SORT_HISTORY: string[] = []

type L2RTableProps = {
    rows: any[]
    columns: GridColDef[]
    pageSize?: number
    emptyText?: string | ReactNode
    defaultSortFields?: string[]
    sortModel?: GridSortModel
    sortHistory?: string[]
    onSortChange?: (_sortModel: GridSortModel, _sortHistory: string[]) => void
} & Omit<DataGridProps, "rows" | "columns" | "sortModel" | "onSortModelChange">

const defaultSortComparator: GridComparatorFn = (v1, v2) => {
    if (v1 == null && v2 == null) return 0
    if (v1 == null) return -1
    if (v2 == null) return 1
    if (typeof v1 === "number" && typeof v2 === "number") return v1 - v2
    return String(v1).localeCompare(String(v2))
}

const buildSortComparator = (
    column: GridColDef,
    columnsByField: Record<string, GridColDef>,
    sortHistory: string[]
): GridComparatorFn => {
    const primaryComparator = column.sortComparator ?? defaultSortComparator
    const secondaryFields = sortHistory.filter((field) => field !== column.field)

    return (v1, v2, cellParams1, cellParams2) => {
        const primaryResult = primaryComparator(v1, v2, cellParams1, cellParams2)
        if (primaryResult !== 0) {
            return primaryResult
        }

        for (const field of secondaryFields) {
            const secondaryComparator = columnsByField[field]?.sortComparator ?? defaultSortComparator
            const secondaryValue1 = cellParams1.api.getCellValue(cellParams1.id, field)
            const secondaryValue2 = cellParams2.api.getCellValue(cellParams2.id, field)
            const secondaryResult = secondaryComparator(secondaryValue1, secondaryValue2, cellParams1, cellParams2)
            if (secondaryResult !== 0) {
                return secondaryResult
            }
        }

        return 0
    }
}

export const L2RTable = (props: L2RTableProps) => {
    const {
        rows,
        columns,
        pageSize = DEFAULT_PAGE_SIZE,
        emptyText,
        defaultSortFields,
        sortModel: controlledSortModel,
        sortHistory: controlledSortHistory,
        onSortChange
    } = props

    const isSortControlled = controlledSortModel !== undefined

    const [internalSortModel, setInternalSortModel] = useState<GridSortModel>(
        defaultSortFields?.length ? [{ field: defaultSortFields[0], sort: "asc" }] : []
    )
    const [internalSortHistory, setInternalSortHistory] = useState<string[]>(defaultSortFields ?? [])

    const sortModel = isSortControlled ? controlledSortModel : internalSortModel
    const sortHistory = isSortControlled ? (controlledSortHistory ?? EMPTY_SORT_HISTORY) : internalSortHistory

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        const previousField = sortModel[0]?.field
        const newSortHistory = newSortModel.length === 0
            ? (previousField ? sortHistory.filter((field) => field !== previousField) : sortHistory)
            : [newSortModel[0].field, ...sortHistory.filter((field) => field !== newSortModel[0].field)]

        if (isSortControlled) {
            onSortChange?.(newSortModel, newSortHistory)
        } else {
            setInternalSortHistory(newSortHistory)
            setInternalSortModel(newSortModel)
        }
    }, [sortModel, sortHistory, isSortControlled, onSortChange])

    const columnsWithSort = useMemo(() => {
        const columnsByField = Object.fromEntries(columns.map((column) => [column.field, column]))

        return columns.map((column) => ({
            ...(column.flex == null && column.width == null ? { flex: 1 } : {}),
            ...column,
            sortComparator: buildSortComparator(column, columnsByField, sortHistory)
        }))
    }, [columns, sortHistory])

    return <Styled.DataGrid
        rows={rows}
        columns={columnsWithSort}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        initialState={{
            pagination: {
                paginationModel: {
                    pageSize
                },
            },
        }}
        getRowClassName={(params) => params.row["disabled"] ? "L2RTable-row--disabled" : ""}
        disableRowSelectionOnClick
        disableColumnMenu
        sortingOrder={["asc", "desc"]}
        rowHeight={ROW_HEIGHT + ROW_SPACING}
        columnHeaderHeight={ROW_HEIGHT}
        hideFooter={rows.length <= pageSize}
        slots={{
            columnSortedAscendingIcon: ArrowDropUpIcon as NonNullable<DataGridProps["slots"]>["columnSortedAscendingIcon"],
            columnSortedDescendingIcon: ArrowDropDownIcon as NonNullable<DataGridProps["slots"]>["columnSortedDescendingIcon"],
            pagination: DataGridPagination,
            noRowsOverlay: () => <Styled.NoRowsOverlay>
                {emptyText}
            </Styled.NoRowsOverlay>
        }}
    />
}